import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api, type BrandMedia } from '../api';
import { t } from '../i18n';
import { fmtBytes, fmtShort } from '../lib/format';
import { useSession } from '../lib/session';
import { uploadBrandMedia, type Progress } from '../lib/upload';
import { Chip, Empty, ErrorBox, errorMessage, Spinner, useConfirm, useToast } from './ui';
import { Icon } from './icons';
import '../styles/features.css';

/**
 * What the agent sees in one original, and a person's correction of it. The agent may say about the photo only what this says, so it is
 * shown on the card and a correction replaces it for good.
 */
function AgentSees({ media, canCorrect, onSaved }: { media: BrandMedia; canCorrect: boolean; onSaved: () => void }) {
  const toast = useToast();
  const d = media.description;
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [tags, setTags] = useState('');
  const save = useMutation({
    mutationFn: () => api.post(`/api/media/${media.id}/description`, { description: text.trim(), tags: tags.split(',').map((x) => x.trim()).filter(Boolean) }),
    onSuccess: () => { setEditing(false); onSaved(); toast(t('settings.media.describedToast')); },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  // A person's correction is theirs alone, tags included: the agent's tags are shown as a suggestion, never carried over unseen, so a
  // text-only correction cannot keep a tag that contradicts it (the agent writes from text and tags alike).
  const suggested = d?.by === 'agent' ? d.tags : [];
  const start = () => { setText(d?.text ?? ''); setTags(d?.by === 'person' ? d.tags.join(', ') : ''); setEditing(true); };
  if (editing) {
    return (
      <form className="fx-media-sees" onSubmit={(e) => { e.preventDefault(); if (text.trim()) save.mutate(); }}>
        <strong className="small">{t('settings.media.sees')}</strong>
        <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} aria-label={t('settings.media.descriptionAria')} maxLength={1000} />
        <input value={tags} onChange={(e) => setTags(e.target.value)} aria-label={t('settings.media.tagsAria')} placeholder={t('settings.media.tagsAria')} />
        {suggested.length > 0 && <p className="small muted">{t('settings.media.agentSuggested', { tags: suggested.join(', ') })}</p>}
        <div className="fx-media-actions">
          <button type="submit" className="btn btn-small btn-primary" disabled={!text.trim() || save.isPending}>{t('settings.media.save')}</button>
          <button type="button" className="btn btn-small btn-ghost" onClick={() => setEditing(false)}>{t('settings.media.cancel')}</button>
        </div>
      </form>
    );
  }
  if (!d && !media.permission.allowed) return null;
  return (
    <div className="fx-media-sees">
      <strong className="small">{t('settings.media.sees')}</strong>
      {d ? (
        <>
          <p className="small">{d.text}</p>
          {d.tags.length > 0 && <p className="small muted">{d.tags.map((x) => `#${x}`).join(' ')}</p>}
          <p className="small muted">{d.by === 'person' ? t('settings.media.seesByPerson', { who: d.who ?? '', when: fmtShort(d.at) }) : t('settings.media.seesByAgent', { when: fmtShort(d.at) })}</p>
        </>
      ) : (
        <p className="small muted">{t('settings.media.seesPending')}</p>
      )}
      {canCorrect && <div><button type="button" className="btn btn-small" onClick={start}>{d ? t('settings.media.correct') : t('settings.media.describe')}</button></div>}
    </div>
  );
}

/**
 * The brand's media library: the client's own photos and videos, uploaded once, each with a plain decision about whether the agent may
 * use it in drafts. Uploading is not permission; the decision is a person's, recorded with who and when, and withdrawable.
 */
export function MediaLibrary({ brandId, compact = false }: { brandId: string; compact?: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { can } = useSession();
  const decides = can('approve');
  const { data, error } = useQuery({ queryKey: ['media', brandId], queryFn: () => api.get<BrandMedia[]>(`/api/brands/${brandId}/media`) });
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['media', brandId] });

  const upload = useMutation({
    mutationFn: () => uploadBrandMedia(brandId, file!, setProgress),
    onSuccess: () => { setFile(null); setProgress(null); if (input.current) input.current.value = ''; refresh(); toast(t('settings.media.uploaded')); },
    onError: (e) => { setProgress(null); toast(errorMessage(e), 'error'); },
  });
  const decide = useMutation({
    mutationFn: ({ id, allowed }: { id: string; allowed: boolean }) => api.post(`/api/media/${id}/permission`, { allowed }),
    onSuccess: (_r, v) => { refresh(); toast(v.allowed ? t('settings.media.allowedToast') : t('settings.media.withdrawnToast')); },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const archive = useMutation({
    mutationFn: (id: string) => api.post(`/api/media/${id}/archive`),
    onSuccess: () => { refresh(); toast(t('settings.media.archivedToast')); },
    onError: (e) => toast(errorMessage(e), 'error'),
  });

  const live = (data ?? []).filter((m) => !m.archived_at);
  const allowedCount = live.filter((m) => m.permission.allowed).length;
  const busy = upload.isPending;
  const pct = progress ? Math.round(progress.fraction * 100) : 0;

  return (
    <>
      <section className="card set-card" aria-label={compact ? 'Photos' : undefined} aria-labelledby={compact ? undefined : 'set-media'} hidden={compact && !!data && live.length === 0 && !error}>
        {!compact && <header className="set-card-top">
          <h3 id="set-media">{t('settings.media.title')}</h3>
          <p className="muted small">{t('settings.media.intro')}</p>
        </header>}
        {error && <ErrorBox error={error} />}
        {!data && !error && <Spinner />}
        {!compact && data && live.length === 0 && <Empty title={t('settings.media.empty')}>{t('settings.media.emptyHint')}</Empty>}
        {live.length > 0 && (
          <>
            <p className="small muted">{t('settings.media.allowedCount', { allowed: allowedCount, total: live.length })}</p>
            <ul className="fx-media-grid">
              {live.map((m) => (
                <li key={m.id} className="fx-media-card" data-allowed={m.permission.allowed || undefined}>
                  <div className="fx-media-pic">
                    {m.kind === 'image' && m.url ? <img src={m.url} alt="" /> : <span className="fx-media-video"><Icon name="film" /></span>}
                    <span className="fx-media-kind">{m.kind === 'video' ? t('settings.media.video') : t('settings.media.image')}</span>
                  </div>
                  <div className="fx-media-body">
                    <div className="ent-title" title={m.name}>{m.name}</div>
                    <div className="ent-sub">{fmtBytes(m.bytes)}{m.width && m.height ? ` · ${m.width}×${m.height}` : ''}{m.duration_ms ? ` · ${Math.round(m.duration_ms / 1000)} s` : ''}</div>
                    {m.permission.allowed ? (
                      <p className="small fx-media-perm"><Chip state="approved" label={t('settings.media.allowed')} /> {t('settings.media.allowedBy', { who: m.permission.by ?? '', when: m.permission.at ? fmtShort(m.permission.at) : '' })}</p>
                    ) : (
                      <p className="small muted fx-media-perm">{m.permission.at ? t('settings.media.withdrawnBy', { who: m.permission.by ?? '', when: fmtShort(m.permission.at) }) : t('settings.media.notAllowed')}</p>
                    )}
                    <AgentSees media={m} canCorrect={decides} onSaved={refresh} />
                    <p className="small muted">{t('settings.media.uploadedBy', { who: m.uploaded_by ?? '', when: m.uploaded_at ? fmtShort(m.uploaded_at) : '' })}</p>
                    {decides && (
                      <div className="fx-media-actions">
                        {m.permission.allowed ? (
                          <button type="button" className="btn btn-small" onClick={() => decide.mutate({ id: m.id, allowed: false })} disabled={decide.isPending}>{t('settings.media.withdraw')}</button>
                        ) : (
                          <button type="button" className="btn btn-small btn-primary" onClick={() => decide.mutate({ id: m.id, allowed: true })} disabled={decide.isPending}>{t('settings.media.allow')}</button>
                        )}
                        <button type="button" className="btn btn-small btn-ghost" onClick={async () => { if (await confirm({ title: t('settings.media.archiveTitle', { name: m.name }), text: t('settings.media.archiveConfirm'), confirmLabel: t('settings.media.archive'), danger: true })) archive.mutate(m.id); }}>{t('settings.media.archive')}</button>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
      {decides && (
        <form className="card set-card" onSubmit={(e) => { e.preventDefault(); if (file) upload.mutate(); }} aria-label={compact ? 'Upload photo' : undefined} aria-labelledby={compact ? undefined : 'set-media-upload'}>
          {!compact && <><header className="set-card-top"><h3 id="set-media-upload">{t('settings.media.uploadTitle')}</h3></header>
          <p className="small muted">{t('settings.media.uploadHint')}</p></>}
          <input ref={input} type="file" accept="image/png,image/jpeg,video/mp4" onChange={(e) => setFile(e.target.files?.[0] ?? null)} disabled={busy} aria-label={t('settings.media.fileAria')} />
          {progress && <p className="small muted" role="status">{t(`settings.media.progress.${progress.step}` as 'settings.media.progress.hashing', { pct })}</p>}
          <div className="row">
            <button type="submit" className="btn btn-primary" disabled={!file || busy}>{busy ? t('settings.media.uploading') : t('settings.media.upload')}</button>
          </div>
        </form>
      )}
    </>
  );
}
